import type { CSSProperties, ReactNode } from 'react'
import { cn } from '../lib'
import { designTokens } from '../lib/design-tokens'
import { contentGroupVariants } from './content-group-variants'
import type {
  ContentGroupAlign,
  ContentGroupBlur,
  ContentGroupBorder,
  ContentGroupGap,
  ContentGroupJustify,
  ContentGroupPadding,
  ContentGroupRadius,
  ContentGroupSurface,
  ContentGroupVariantId,
} from './content-group.types'

export type ContentGroupProps = {
  variant?: ContentGroupVariantId
  surface?: ContentGroupSurface
  padding?: ContentGroupPadding
  radius?: ContentGroupRadius
  gap?: ContentGroupGap
  border?: ContentGroupBorder
  blur?: ContentGroupBlur
  align?: ContentGroupAlign
  justify?: ContentGroupJustify
  className?: string
  style?: CSSProperties
  children: ReactNode
}

const surfaceMap: Record<ContentGroupSurface, string> = {
  'solid-white': designTokens.colors.background.white,
  'solid-surface': designTokens.colors.background.surface,
  'glass-90': designTokens.colors.background.glassStrong,
  'glass-70': designTokens.colors.background.glassMedium,
  'glass-50': designTokens.colors.background.glass,
  'ghost-10': designTokens.colors.background.glassSoft,
  'info-soft': designTokens.colors.background.page,
  'catalog-subscription-panel': designTokens.colors.background.catalogSubscriptionPanel,
  transparent: designTokens.colors.background.transparent,
}

const paddingMap: Record<ContentGroupPadding, string> = {
  'lg-compact': `${designTokens.spacing.xxxl} ${designTokens.spacing.xxl}`,
  'mobile-sheet': `${designTokens.spacing.xxxl} ${designTokens.spacing.mobilePanelX}`,
  'md-compact': `${designTokens.spacing.xxl} ${designTokens.spacing.mobilePanelX}`,
  'md-wide': `${designTokens.spacing.xl} ${designTokens.spacing.xxxl}`,
  panel: `${designTokens.spacing.xxl} ${designTokens.spacing.xxxl}`,
  sm: designTokens.spacing.lg,
  'sm-compact': `${designTokens.spacing.lg} ${designTokens.spacing.md}`,
  md: `${designTokens.spacing.xxl} ${designTokens.spacing.lg}`,
}

const radiusMap: Record<ContentGroupRadius, string> = {
  md: designTokens.borders.radius.md,
  lg: designTokens.borders.radius.lg,
  xl: designTokens.borders.radius.xl,
  'xl-top': designTokens.borders.radius.xlTop,
}

const gapMap: Record<ContentGroupGap, string> = {
  none: designTokens.spacing.none,
  '12': designTokens.spacing.md,
  '16': designTokens.spacing.lg,
  '23': designTokens.spacing.popupModalGap,
  '24': designTokens.spacing.xxl,
}

const borderMap: Record<ContentGroupBorder, { color: string; width: string; style: string }> = {
  none: {
    color: designTokens.colors.background.transparent,
    width: designTokens.borders.widths.none,
    style: designTokens.borders.styles.solid,
  },
  glass: {
    color: designTokens.colors.border.glass,
    width: designTokens.borders.widths.thin,
    style: designTokens.borders.styles.solid,
  },
  white: {
    color: designTokens.colors.border.white,
    width: designTokens.borders.widths.thin,
    style: designTokens.borders.styles.solid,
  },
  info: {
    color: designTokens.colors.border.paymentDefault,
    width: designTokens.borders.widths.thin,
    style: designTokens.borders.styles.solid,
  },
  'catalog-subscription-panel': {
    color: designTokens.colors.border.catalogSubscriptionPanel,
    width: designTokens.borders.widths.thin,
    style: designTokens.borders.styles.solid,
  },
}

const blurMap: Record<ContentGroupBlur, string | undefined> = {
  none: undefined,
  soft: designTokens.effects.blur.subscriptionBenefitCard,
}

const alignMap: Record<ContentGroupAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
}

const justifyMap: Record<ContentGroupJustify, string> = {
  start: 'justify-start',
  between: 'justify-between',
  end: 'justify-end',
}

export function ContentGroup({
  variant,
  surface,
  padding,
  radius,
  gap,
  border,
  blur,
  align,
  justify,
  className,
  style: styleOverride,
  children,
}: ContentGroupProps) {
  const variantConfig = variant ? contentGroupVariants[variant] : undefined
  const resolvedSurface = surface ?? variantConfig?.surface ?? 'solid-surface'
  const resolvedPadding = padding ?? variantConfig?.padding ?? 'sm'
  const resolvedRadius = radius ?? variantConfig?.radius ?? 'lg'
  const resolvedGap = gap ?? variantConfig?.gap ?? 'none'
  const resolvedBorder = border ?? variantConfig?.border ?? 'none'
  const resolvedBlur = blur ?? variantConfig?.blur ?? 'none'
  const resolvedAlign = align ?? variantConfig?.align ?? 'start'
  const resolvedJustify = justify ?? variantConfig?.justify ?? 'start'

  const borderTokens = borderMap[resolvedBorder]
  const blurValue = blurMap[resolvedBlur]

  const style: CSSProperties = {
    background: surfaceMap[resolvedSurface],
    padding: paddingMap[resolvedPadding],
    borderRadius: radiusMap[resolvedRadius],
    borderColor: borderTokens.color,
    borderWidth: borderTokens.width,
    borderStyle: borderTokens.style,
    gap: gapMap[resolvedGap],
    backdropFilter: blurValue ? `blur(${blurValue})` : undefined,
    WebkitBackdropFilter: blurValue ? `blur(${blurValue})` : undefined,
    ...styleOverride,
  }

  return (
    <div
      className={cn(
        'flex flex-col',
        alignMap[resolvedAlign],
        justifyMap[resolvedJustify],
        className
      )}
      style={style}
    >
      {children}
    </div>
  )
}
